import React, { Suspense } from 'react';
import { navigate } from 'gatsby';
import queryString from 'query-string';
import { useLocation, WindowLocation } from '@reach/router';

import { hasPermission } from '$/utils/hasPermission';
import { useMeQuery, SubscriptionStatus, SubscriptionSubstatus } from '$/GraphQL/schema/schema';
import Loading from './Loading';
import { PermissionProvider } from './Permission';
import { MeContext, useMe } from '$/hooks/useMe';
import { useServerContext } from '$/hooks/useServerDateNow';
import config from '$/config';
import { rolesPermissionsMatrix } from '$/shared/frontend/rolesPermissionsMatrix';

const TeamUserPilot = React.lazy(() => import('./Userpilot'));

export * from './RedirectIfUnauthenticated';

export const RedirectIfNoPermission: React.FC = () => {
  const me = useMe();
  const { pathname } = useLocation();
  React.useEffect(() => {
    const canAccess = hasPermission(pathname, me);
    if (!canAccess) {
      navigate('/home');
    }
  }, [me, pathname]);

  return null;
};

// only access if not logged in
const onlyAccessIfNotLoggedIn = [/*RegExp(/^\/signup/, 'i'), */ RegExp(/^\/login/, 'i')];

// can access in with

interface AccessCheckerProps {
  noAuth: boolean;
  location: WindowLocation;
  children?: React.ReactNode;
}

const useDeprecatedLinkRedirects = () => {
  const { pathname } = useLocation();
  const deprecatedPaths = React.useMemo(() => {
    return Object.values(rolesPermissionsMatrix).filter(({ oldPath }) => Boolean(oldPath));
  }, []);

  React.useEffect(() => {
    const matched = deprecatedPaths.find(({ oldPath }) => oldPath && pathname.startsWith(oldPath));
    if (matched) {
      navigate(matched.path);
    }
  }, [deprecatedPaths, pathname]);
};

const AccessChecker: React.FC<AccessCheckerProps> = ({
  children,
  noAuth, // true - non authorized users can view this
  location,
}) => {
  // anyone can view this page i.e. (no auth page and both
  // logged in and logged out users can view it)
  const anyoneCanViewIt =
    noAuth &&
    location &&
    onlyAccessIfNotLoggedIn.findIndex((oneRoute) => oneRoute.test(location.pathname)) === -1
      ? true
      : false;

  // get user info
  const meQuery = useMeQuery({
    // skip if no it is a no auth page and both logged in and logged out users can view it
    skip: anyoneCanViewIt || config.isSSR,
  });
  useDeprecatedLinkRedirects();

  // user data exist
  const userDataExist = Boolean(meQuery.data?.me && !meQuery.error);
  const me = meQuery.data?.me;
  const isUnVerified =
    me?.team.subscriptionStatus === SubscriptionStatus.Incomplete &&
    me?.team.subscriptionSubstatus === SubscriptionSubstatus.NoSubscription;
  const isSuspended =
    me?.team.subscriptionStatus &&
    !isUnVerified &&
    [SubscriptionStatus.Suspended, SubscriptionStatus.Incomplete].includes(
      me.team.subscriptionStatus
    );
  const isProfileComplete = Boolean(me?.details?.onboardCompletedAt);

  const { setServerDate } = useServerContext();
  React.useEffect(() => {
    if (!me?.now) return;
    setServerDate(new Date(me.now));
  }, [me?.now, setServerDate]);

  if (anyoneCanViewIt) {
    // anyone can view these pages
  } else if (noAuth && !anyoneCanViewIt) {
    // this page should only be visible if user is not logged in
    if (userDataExist) {
      // if user is logged in then redirect
      navigate('/home');

      return <Loading />;
    } else if (!meQuery.called || meQuery.loading) {
      // loading state
      return <Loading />;
    }
  } else {
    // this page should only be visible if user is logged in
    if (!meQuery.called || meQuery.loading || config.isSSR) {
      // loading state
      return <Loading />;
    } else if (!userDataExist) {
      // if user is not logged in then redirect
      navigate(
        `/login?redirect=${encodeURIComponent(location.pathname + location.search + location.hash)}`
      );
      return <Loading />;
    } else if (me && !isProfileComplete && !me.impersonatingUser) {
      if (!location.pathname.includes('/onboard') && !location.pathname.includes('/invited')) {
        if (me?.isInvitedUser) {
          navigate('/invited');
        } else {
          navigate('/onboard');
        }
        return <Loading />;
      }
    } else if (isSuspended) {
      // team suspended
      // if can access to plan page redirect to plan page
      // else redirect to home
      const canAccess =
        meQuery.data?.me &&
        hasPermission(rolesPermissionsMatrix.accountSubscription.path, meQuery.data?.me);
      if (
        canAccess &&
        ![
          rolesPermissionsMatrix.accountSubscription.path,
          rolesPermissionsMatrix.accountSubscription.path + '/',
        ].includes(location.pathname)
      ) {
        navigate(rolesPermissionsMatrix.accountSubscription.path);
      }
      if (
        !canAccess &&
        ![rolesPermissionsMatrix.home.path, rolesPermissionsMatrix.home.path + '/'].includes(
          location.pathname
        )
      ) {
        navigate(rolesPermissionsMatrix.home.path);
      }
    } else {
      const parsed = queryString.parse(location.search);
      if (parsed.redirect && typeof window !== `undefined`) {
        const redirect = decodeURIComponent(parsed.redirect as string);

        if (redirect.startsWith('/')) {
          navigate(redirect);
        } else {
          // if full URL is specified, take user to that page instead
          document.location.href = redirect;
        }
      }
    }
  }

  return noAuth || anyoneCanViewIt ? (
    <>{children}</>
  ) : !me ? (
    <Loading query={meQuery} />
  ) : (
    <>
      {/* permission should only be checked for logged in routes */}
      <MeContext.Provider value={me}>
        <RedirectIfNoPermission />
        <PermissionProvider>{children}</PermissionProvider>
      </MeContext.Provider>
      <Suspense>
        <TeamUserPilot me={meQuery.data!.me ?? undefined} />
      </Suspense>
    </>
  );
};

export default AccessChecker;
